import { AddressEdit } from './shop/AddressEdit';
import { KycPanel } from './shop/KycPanel';
import { TeamPanel } from './shop/TeamPanel';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useCart, useSetCartLine, useSetCartLines } from '../api/hooks';
import type { ShopAccount, ShopAddress, ShopCatalogCard, ShopVariant, ShopVariantDetail as ShopVariantDetailData, Statement } from '../api/types';
import { useSession } from '../auth/session';
import { TrustedDevices } from './TrustedDevices';
import {
  Alert, Button, Chip, Empty, Field, IndicativePrice, Money, Note, PageHeader, Panel, Rail, Spinner, Table,
} from '../ui';
import {
  activeMetalFields, activeStoneFields, isStoneSelectionEmpty, METAL_FIELDS, matchingStoneSiblings,
  stoneValuesFor, unansweredFields, STONE_FIELDS,
  type StoneField, type StoneSelection, type StoneSibling, type VariantField,
  selectOne,
} from './variant-selector';
/* D75 — the ONE due-rule formatter. No screen builds a "when is this due" sentence itself. */
import { dueRuleSourceWords, dueRuleWords } from './payables-view';
import { syncedQty } from './shop/add-to-cart';
import { mulMoney, scaled, sumLines } from './shop/line-money';
import { Lightbox } from './shop/Lightbox';
import { ThumbPreview } from './shop/ThumbPreview';
import { trimDecimal as trimAmount } from '../format';

/**
 * R-02, R-03, R-16, R-18 — the customer-facing screens.
 *
 * Every string here comes from the catalog (D47, CR-02). That is the boundary:
 * these four screens and the public pages are translated, the admin console is
 * not, and an ESLint rule holds the line rather than a convention.
 */

/** Grams and carats are decimal strings from the API and never become numbers. */
const dash = '—';

/** A stable, empty fallback for `data?.stoneOptions` while a variant is
 *  still loading — a fresh `{}` literal every render would defeat the
 *  `useMemo` that reads it. */
const EMPTY_STONE_OPTIONS: ShopVariantDetailData['stoneOptions'] = {
  stoneOrigin: [], gemstone: [], stoneShape: [], stoneCut: [], stoneColour: [], stoneClarity: [],
};

/**
 * Facet swatch colours — the reference's own `HUE`/`TINT` maps
 * (`retailer-portal.html:953, 1456`), reused verbatim rather than re-deriving
 * new hex values. Both facets can carry a value these maps don't name (the
 * catalog is data, the maps are the prototype's fixed sample set), so each
 * falls back to a neutral swatch rather than rendering nothing.
 */
const COLOUR_HUE: Record<string, string> = { Yellow: '#D9B45B', White: '#DBDBDF', Rose: '#DBA79B' };
const COLLECTION_TINT: Record<string, string> = {
  Aurora: '#F3ECEF', Bubble: '#F5F0E9', Diana: '#EFF0F3', Noor: '#F2EFEA',
  Pearl: '#F4F2F0', Soleil: '#F6F1E7', Trinity: '#F0F1EF',
};
const SWATCH_FALLBACK = '#F2EFEC';

/** "0.5600" (NUMERIC(19,4)) shown as "0.56ct" — truncation for display, never
 *  arithmetic, so a plain string slice is correct and no float is involved. */
function carats(v: string): string {
  const [whole = '0', frac = ''] = v.split('.');
  return `${whole}.${frac.padEnd(2, '0').slice(0, 2)}ct`;
}

/** The centre-stone carat a variant is DEFINED by, from its code: "030" is
 *  0.30 ct, "050" 0.50 ct — hundredths, as the admin form's own hint and the
 *  master list's labels both have it. Anything not three digits is shown as
 *  stored rather than guessed at. Not `totalCaratWeight`: that is every stone
 *  summed, can be blank, and is not what tells two pieces apart. */
function caratFromCode(code: string | null): string | null {
  if (!code) return null;
  return /^\d{3}$/.test(code) ? `${code[0]}.${code.slice(1)} ct` : code;
}

/**
 * The stepper + "Add to order" control shared by R-02's rows and R-03's panel.
 *
 * `cartQty` is the quantity already on the cart for this variant (0 if none) —
 * read from `GET /shop/cart`, the same query `screens/shop/Order.tsx` reads, so
 * the two screens never disagree about what is on the order. No MOQ, no
 * availability check (D4, CR-07): any quantity from 1 upward is valid, and 0
 * removes the line (`zSetCartLine` in `@miraki/contracts`).
 */
function AddToCart({ code, cartQty, canOrder }: { code: string; cartQty: number; canOrder: boolean }) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(cartQty);
  const setLine = useSetCartLine();

  /*
   * The stepper follows the cart when the cart moves and the retailer has not typed since —
   * `shop/add-to-cart.ts`. Seeded once at mount, this control's state was a second answer to
   * "how many of this are on the order", and the button is offered by comparing the two: a
   * stepper left behind by any cart change elsewhere both lied about the order and, pressed,
   * sent the figure it was still showing. Adjusting state during render rather than in an
   * effect is React's own answer for a value derived from a prop — no extra render, and no
   * frame in which the row shows a number the cart has already contradicted.
   */
  const [lastSeen, setLastSeen] = useState(cartQty);
  if (lastSeen !== cartQty) {
    setLastSeen(cartQty);
    setQty(syncedQty({ typed: qty, lastSeen, cartQty }));
  }

  if (!canOrder) {
    return cartQty > 0
      ? <span className="mk-dim">{t('shop.addToCart.inOrder', { quantity: cartQty })}</span>
      : <span className="mk-dim">{t('shop.addToCart.viewOnly')}</span>;
  }

  const step = (n: number) => setQty(Math.max(0, n));

  return (
    <div className="mk-addcart">
      <span className="mk-qty">
        <button
          type="button"
          disabled={qty <= 0}
          onClick={() => step(qty - 1)}
          aria-label={t('shop.addToCart.fewer')}
        >−</button>
        <input
          inputMode="numeric"
          value={qty === 0 ? '' : String(qty)}
          placeholder="0"
          aria-label={t('shop.addToCart.qtyFor', { code })}
          onChange={(e) => step(Math.round(Number(e.target.value.replace(/\D/g, '')) || 0))}
        />
        <button type="button" onClick={() => step(qty + 1)} aria-label={t('shop.addToCart.more')}>+</button>
      </span>
      <Button
        size="sm"
        disabled={setLine.isPending || qty === cartQty}
        onClick={() => setLine.mutate({ variantCode: code, quantity: qty })}
      >
        {cartQty > 0 ? t('shop.addToCart.update') : t('shop.addToCart.add')}
      </Button>
      {setLine.isSuccess && setLine.variables?.variantCode === code && qty === cartQty
        ? <span className="mk-dim">{t('shop.addToCart.added')}</span> : null}
      {setLine.isError && setLine.variables?.variantCode === code
        ? <span className="mk-dim">{setLine.error.message}</span> : null}
    </div>
  );
}

// -------------------------------------------------------------------------- R-02 catalog

/** Search, one facet group each, matched by exact value. Empty array = no filter
 *  on that group (OR within a group, AND across groups — same as the prototype,
 *  `catRows()` at `retailer-portal.html:1489-1497`). */
export interface CatalogFacets {
  collection: string[];
  category: string[];
  purity: string[];
  metalColour: string[];
}

export type CatalogSort = 'new' | 'col' | 'lo' | 'hi' | 'code';

function cmpBigint(a: bigint, b: bigint): number { return a < b ? -1 : a > b ? 1 : 0; }

/** Matches on code, product name and collection (not "your SKU" — the search
 *  box's placeholder only promises those three). Pure, so it's easy to reason
 *  about independently of the component, following `Order.tsx`'s `parsePaste`
 *  precedent — kept file-private rather than exported so this file doesn't mix
 *  component and non-component exports (`react-refresh/only-export-components`,
 *  which `Order.tsx` already trips at `--max-warnings=0` by doing exactly that;
 *  not repeated here). */
/**
 * Metal is matched against EVERY metal the product comes in (`metals`), not
 * only the card's own piece — the card leads with one (usually 10k Yellow),
 * and filtering on that alone hid every design under 18k or Rose. Purity and
 * colour must hold on the SAME piece: a design made in 18k Yellow and 10k
 * White is not an "18k White".
 *
 * When the card's own piece is not the match, the card becomes the matching
 * piece — its code, price and metal — so the price shown, the link and the
 * card's own Add to order all mean the metal that was asked for. Fields that
 * belong to the old piece only (photos scoped to its colour, weights, stone)
 * are dropped rather than shown against the wrong metal.
 */
function filterCatalog(rows: ShopCatalogCard[], query: string, facets: CatalogFacets): ShopCatalogCard[] {
  const q = query.trim().toLowerCase();
  const metalOk = (purity: string | null, colour: string | null) =>
    (!facets.purity.length || facets.purity.includes(purity ?? ''))
    && (!facets.metalColour.length || facets.metalColour.includes(colour ?? ''));
  const out: ShopCatalogCard[] = [];
  for (const v of rows) {
    if (q) {
      const hay = `${v.baseCode} ${v.code} ${v.product ?? ''} ${v.collection ?? ''}`.toLowerCase();
      if (!hay.includes(q)) continue;
    }
    if (facets.collection.length && !facets.collection.includes(v.collection ?? '')) continue;
    if (facets.category.length && !facets.category.includes(v.category ?? '')) continue;
    if (metalOk(v.purity, v.metalColour)) { out.push(v); continue; }
    const m = v.metals.find((x) => metalOk(x.purity, x.metalColour));
    if (!m) continue;
    out.push({
      ...v,
      code: m.code,
      listPrice: m.listPrice,
      purity: m.purity,
      metalColour: m.metalColour,
      puritySortOrder: m.puritySortOrder,
      metalColourSortOrder: m.metalColourSortOrder,
      imageUrls: m.metalColour === v.metalColour ? v.imageUrls : [],
      caratCode: null, netMetalWeightG: null, grossWeightG: null, totalCaratWeight: null,
      stoneOrigin: null, gemstone: null, stoneShape: null, stoneCut: null,
      stoneColour: null, stoneClarity: null,
      externalSku: null,
    });
  }
  return out;
}

/** Purity and colour facets, counted per PRODUCT across every metal it comes
 *  in — the same reading `filterCatalog` uses, so a count never promises more
 *  or fewer cards than ticking it shows. */
function metalFacetCounts(
  rows: ShopCatalogCard[],
  field: 'purity' | 'metalColour',
): Array<{ value: string; count: number }> {
  const counts = new Map<string, number>();
  const keyOf = new Map<string, number>();
  for (const r of rows) {
    const values = new Set<string>();
    for (const m of r.metals) {
      const val = m[field];
      if (!val) continue;
      values.add(val);
      const k = field === 'purity' ? m.puritySortOrder : m.metalColourSortOrder;
      if (k !== null && !keyOf.has(val)) keyOf.set(val, k);
    }
    for (const val of values) counts.set(val, (counts.get(val) ?? 0) + 1);
  }
  return orderedValues(Array.from(counts.keys()), keyOf).map((value) => ({ value, count: counts.get(value)! }));
}

/** Returns a new array — the one react-query owns is never mutated in place. */
function sortCatalog<T extends ShopVariant>(rows: T[], sort: CatalogSort): T[] {
  const out = [...rows];
  // 'new': the API already returns products newest-first by creation time, and
  // `filterCatalog` preserves order, so there is nothing to re-sort here.
  if (sort === 'new') return out;
  if (sort === 'lo') out.sort((a, b) => cmpBigint(scaled(a.listPrice), scaled(b.listPrice)));
  else if (sort === 'hi') out.sort((a, b) => cmpBigint(scaled(b.listPrice), scaled(a.listPrice)));
  else if (sort === 'code') out.sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
  else {
    out.sort((a, b) => {
      const ca = a.collection ?? '';
      const cb = b.collection ?? '';
      if (ca !== cb) return ca < cb ? -1 : 1;
      const pa = a.product ?? '';
      const pb = b.product ?? '';
      return pa < pb ? -1 : pa > pb ? 1 : 0;
    });
  }
  return out;
}

/**
 * Facet values and their counts, computed against the **full, unfiltered**
 * catalog every time — not re-narrowed by whatever else is currently checked.
 * That's what the prototype draws (`retailer-portal.html:1476-1477`) and the
 * implementation spec says to match it rather than "improve" it: a count here
 * answers "how many rows carry this value", not an availability quantity (D4).
 */
/**
 * Alphabetical is the right default (Collection, Category) and it's what the
 * reference actually draws for those two. Purity and Metal colour are not:
 * they carry a real admin-defined order (`mki_attribute_values.sort_order` —
 * purity ascending by fineness, colour Yellow/White/Rose), and guessing at it
 * from the label lands backwards — parsing "18k" and sorting descending, or
 * sorting "Rose"/"White"/"Yellow" alphabetically, are both the exact reverse
 * of what Admin actually set (confirmed against the shared Dev data). `keyOf`
 * carries that real order when the caller has it (`puritySortOrder`/
 * `metalColourSortOrder`, threaded through from the API); values it doesn't
 * cover fall back to alphabetical rather than guessing.
 */
function orderedValues(values: string[], keyOf: Map<string, number>): string[] {
  if (keyOf.size >= values.length && values.every((v) => keyOf.has(v))) {
    return [...values].sort((a, b) => keyOf.get(a)! - keyOf.get(b)!);
  }
  return [...values].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}


function facetCounts(
  rows: ShopVariant[],
  pick: (v: ShopVariant) => string | null,
  pickSortKey?: (v: ShopVariant) => number | null,
): Array<{ value: string; count: number }> {
  const counts = new Map<string, number>();
  const keyOf = new Map<string, number>();
  for (const r of rows) {
    const val = pick(r);
    if (val === null || val === '') continue;
    counts.set(val, (counts.get(val) ?? 0) + 1);
    if (pickSortKey && !keyOf.has(val)) {
      const k = pickSortKey(r);
      if (k !== null) keyOf.set(val, k);
    }
  }
  return orderedValues(Array.from(counts.keys()), keyOf).map((value) => ({ value, count: counts.get(value)! }));
}

/**
 * "Last viewed" (implementation spec §2/§4) — the prototype's version is
 * in-memory only; this is the real thing, in `localStorage`, most-recent-first
 * and capped at 10 so the strip stays a glance rather than a second catalog.
 * No cap is specified anywhere in the design system (checked
 * `prototypes/src/design-system.html` — no precedent), so 10 is a plain,
 * round choice inside the 8–12 range the implementation spec allows.
 */
const LAST_VIEWED_KEY = 'miraki.shop.lastViewed';
const LAST_VIEWED_CAP = 10;

function readLastViewed(): string[] {
  try {
    const raw = localStorage.getItem(LAST_VIEWED_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === 'string') : [];
  } catch {
    // Safari private mode, or storage disabled — the strip is simply empty.
    return [];
  }
}

function recordLastViewed(code: string): void {
  try {
    const next = [code, ...readLastViewed().filter((c) => c !== code)].slice(0, LAST_VIEWED_CAP);
    localStorage.setItem(LAST_VIEWED_KEY, JSON.stringify(next));
  } catch {
    // A browser refusing storage must not break the product page over it.
  }
}

function clearLastViewed(): void {
  try {
    localStorage.removeItem(LAST_VIEWED_KEY);
  } catch {
    // Same as above — a browser refusing storage has nothing to clear anyway.
  }
}

/**
 * The catalog's own search, sort, view and facet ticks — kept alive across
 * an unmount, module-scoped rather than in the URL or storage. This screen
 * unmounts every time a retailer opens a product (Product Details is a
 * different route) and remounts fresh on the way back, and without this a
 * search typed, a facet ticked or Grid switched to List reset to nothing
 * the moment "Back" was clicked — Select All's own precedent, `lastViewedCodes`
 * above, only covers what survives a full page reload; this is the same idea
 * for what only needs to survive this tab's own back-and-forth. Not the URL:
 * nothing here needs to be a shareable link, and query-string encoding for
 * four different shapes is more machinery than this is worth. Not
 * `sessionStorage`: it would also survive a genuine reload, and the effect
 * a stale filter has on a screen a retailer expects to open fresh outweighs
 * surviving a refresh nobody asked this to survive.
 */
let rememberedCatalogState = {
  query: '',
  sort: 'new' as CatalogSort,
  view: 'grid' as 'grid' | 'list',
  facets: { collection: [], category: [], purity: [], metalColour: [] } as CatalogFacets,
};

/** R-02 — the catalog. */
export function ShopCatalog() {
  const { t } = useTranslation();
  const { me } = useSession();
  const { data, isLoading, error } = useQuery({
    queryKey: ['shop', 'catalog'],
    queryFn: () => api.get<ShopCatalogCard[]>('/shop/catalog'),
    // Shorter than the global 30s default (main.tsx) — this list reflects
    // publish/price state an admin controls elsewhere, not the retailer's
    // own actions, so a re-visit or window refocus should pick up a recent
    // admin change quickly rather than trusting a stale copy for half a
    // minute.
    staleTime: 5_000,
  });
  const canOrder = me?.kind === 'retailer' && me.permissions.includes('shop.order');
  const canViewCart = canOrder || (me?.kind === 'retailer' && me.permissions.includes('shop.order_view'));
  const { data: cart } = useCart({ enabled: canViewCart });

  const [query, setQuery] = useState(rememberedCatalogState.query);
  const [sort, setSort] = useState<CatalogSort>(rememberedCatalogState.sort);
  const [view, setView] = useState<'grid' | 'list'>(rememberedCatalogState.view);
  const [facets, setFacets] = useState<CatalogFacets>(rememberedCatalogState.facets);
  // Mirrored into the module-scoped remembered state on every change, so the
  // NEXT mount (returning from a product) picks up where this one left off.
  useEffect(() => {
    rememberedCatalogState = { query, sort, view, facets };
  }, [query, sort, view, facets]);
  // Initializer only: the route unmounts this screen on navigating to a
  // product and remounts it on the way back, which is what re-reads the list —
  // no subscription needed to prove it survives a reload too (it's storage).
  // The setter exists only for "Clear" (below), not for re-reading storage.
  const [lastViewedCodes, setLastViewedCodes] = useState<string[]>(() => readLastViewed());
  const recentTrackRef = useRef<HTMLDivElement>(null);
  const scrollRecent = (dir: 1 | -1) => {
    recentTrackRef.current?.scrollBy({ left: dir * 240, behavior: 'smooth' });
  };

  /*
   * Filtering, sorting and the four facet counts each walk the whole catalog
   * — cheap once, wasteful recomputed on every render this component has no
   * reason to redo it on: switching grid/list, the cart badge refetching,
   * scrolling the recent strip. None of those touch `catalog`, `query`,
   * `facets` or `sort`, so `useMemo` skips the work rather than re-walking
   * the list on every keystroke elsewhere on the same screen. Computed
   * ahead of the loading/error returns below (Rules of Hooks) — `catalog`
   * falls back to `[]` while `data` is still undefined, so there is nothing
   * to walk yet either way.
   */
  const catalog = useMemo(() => data ?? [], [data]);
  const rows = useMemo(
    () => sortCatalog(filterCatalog(catalog, query, facets), sort),
    [catalog, query, facets, sort],
  );
  const facetGroups = useMemo((): Array<{
    key: keyof CatalogFacets; label: string; options: Array<{ value: string; count: number }>;
  }> => [
    { key: 'collection', label: t('shop.catalog.facetCollection'), options: facetCounts(catalog, (v) => v.collection) },
    { key: 'category', label: t('shop.catalog.facetCategory'), options: facetCounts(catalog, (v) => v.category) },
    { key: 'purity', label: t('shop.catalog.facetMetal'), options: metalFacetCounts(catalog, 'purity') },
    { key: 'metalColour', label: t('shop.catalog.facetColour'), options: metalFacetCounts(catalog, 'metalColour') },
  ], [catalog, t]);

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;

  const currency = me?.kind === 'retailer' ? me.retailer.tradingCurrency : undefined;
  const cartQty = new Map(cart?.lines.map((l) => [l.variantCode, l.quantity]));
  const lastViewed = lastViewedCodes
    .map((code) => catalog.find((v) => v.code === code))
    .filter((v): v is ShopCatalogCard => !!v);
  const anySku = rows.some((v) => v.externalSku);

  const toggleFacet = (group: keyof CatalogFacets, value: string) => {
    setFacets((f) => {
      const cur = f[group];
      return {
        ...f,
        [group]: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value],
      };
    });
  };

  const countText = rows.length === catalog.length
    ? t('shop.catalog.countAll', { count: rows.length })
    : t('shop.catalog.countFiltered', { count: rows.length, total: catalog.length });

  const emptyNode = (
    <Empty
      title={catalog.length === 0 ? t('shop.catalog.emptyTitle') : t('shop.catalog.noMatchTitle')}
      body={catalog.length === 0 ? t('shop.catalog.emptyBody') : t('shop.catalog.noMatchBody')}
    />
  );

  /** Grid card, shared by the main results and the last-viewed strip. */
  const renderCard = (v: ShopVariant) => (
    <div className="mk-pcard" key={v.code}>
      {/* D5 no longer blocks this — the product has real photographs once Admin
          publishes one, CR-43-scoped to this variant's own metal colour by the
          API. The tinted diamond placeholder (the reference's own treatment)
          is the fallback for a product nobody has photographed yet, not a
          statement that no image can exist. */}
      {v.imageUrls[0] ? (
        <div className="mk-pcard-img">
          <img src={v.imageUrls[0]} alt="" loading="lazy" />
          <Link className="mk-pcard-hit" to={`/shop/catalog/${v.code}`} tabIndex={-1} aria-hidden="true" />
        </div>
      ) : (
        <div className="mk-pcard-img">
          <i aria-hidden="true" />
          <Link className="mk-pcard-hit" to={`/shop/catalog/${v.code}`} tabIndex={-1} aria-hidden="true" />
        </div>
      )}
      <div className="mk-pcard-b">
        <div className="mk-pcard-col">{[v.collection, v.category].filter(Boolean).join(' · ') || dash}</div>
        <Link className="mk-pcard-n" to={`/shop/catalog/${v.code}`}>{v.product ?? dash}</Link>
        <div className="mk-pcard-at">
          {[v.purity, v.metalColour, v.totalCaratWeight ? carats(v.totalCaratWeight) : null]
            .filter(Boolean).join(' · ') || dash}
        </div>
        <div className="mk-pcard-p"><IndicativePrice amount={v.listPrice} currency={currency} suffix trimZeros /></div>
        <div className="mk-pcard-add">
          <AddToCart code={v.code} cartQty={cartQty.get(v.code) ?? 0} canOrder={!!canOrder} />
        </div>
        <div className="mk-pcard-code">
          <code>{v.baseCode}</code>
          {v.externalSku ? <span className="mk-pcard-sku">{t('shop.catalog.yourSku', { sku: v.externalSku })}</span> : null}
        </div>
      </div>
    </div>
  );

  /** Recently-viewed strip row — a small tinted thumbnail beside the name
   *  and price, nothing else (no collection line, no attributes, no
   *  Add-to-cart, no code). The tint is the same per-collection swatch the
   *  facet sidebar already uses (`COLLECTION_TINT`), not a new colour. */
  const renderRecentItem = (v: ShopVariant) => (
    <Link className="mk-recent-item" to={`/shop/catalog/${v.code}`} key={v.code}>
      <div
        className="mk-recent-thumb"
        style={{ background: (v.collection && COLLECTION_TINT[v.collection]) ?? SWATCH_FALLBACK }}
      >
        {v.imageUrls[0] ? <img src={v.imageUrls[0]} alt="" loading="lazy" /> : <i aria-hidden="true" />}
      </div>
      <div className="mk-recent-info">
        <div className="mk-recent-name">{v.product ?? dash}</div>
        <div className="mk-recent-price"><IndicativePrice amount={v.listPrice} currency={currency} suffix trimZeros /></div>
      </div>
    </Link>
  );

  return (
    <>
      <PageHeader title={t('shop.catalog.title')} />

      <div className="mk-toolbar">
        <input
          className="mk-fld mk-toolbar-search"
          type="search"
          value={query}
          placeholder={t('shop.catalog.searchPlaceholder')}
          aria-label={t('shop.catalog.searchLabel')}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          value={sort}
          aria-label={t('shop.catalog.sortLabel')}
          onChange={(e) => setSort(e.target.value as CatalogSort)}
        >
          <option value="new">{t('shop.catalog.sortNewest')}</option>
          <option value="col">{t('shop.catalog.sortCollection')}</option>
          <option value="lo">{t('shop.catalog.sortPriceLow')}</option>
          <option value="hi">{t('shop.catalog.sortPriceHigh')}</option>
          <option value="code">{t('shop.catalog.sortCode')}</option>
        </select>
        <span className="mk-spacer" />
        <span className="mk-dim">{countText}</span>
        <div className="mk-viewtoggle">
          <button type="button" aria-pressed={view === 'grid'} onClick={() => setView('grid')}>
            {t('shop.catalog.viewGrid')}
          </button>
          <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}>
            {t('shop.catalog.viewList')}
          </button>
        </div>
      </div>

      <Panel>
        <div className="mk-catlayout">
          <div className="mk-facets">
            {facetGroups.filter((g) => g.options.length).map((g) => (
              <div className="mk-facet-grp" key={g.key}>
                <div className="mk-facet-title">{g.label}</div>
                {g.options.map((o) => (
                  <label className="mk-facet-row" key={o.value}>
                    <input
                      type="checkbox"
                      checked={facets[g.key].includes(o.value)}
                      onChange={() => toggleFacet(g.key, o.value)}
                    />
                    {g.key === 'collection' ? (
                      <span className="mk-swatch" style={{ background: COLLECTION_TINT[o.value] ?? SWATCH_FALLBACK }} aria-hidden="true" />
                    ) : null}
                    {g.key === 'metalColour' ? (
                      <span className="mk-swatch" style={{ background: COLOUR_HUE[o.value] ?? SWATCH_FALLBACK }} aria-hidden="true" />
                    ) : null}
                    {o.value}
                    <span className="mk-facet-n">{o.count}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          <div className="mk-catmain">
            {rows.length === 0 ? emptyNode : view === 'grid' ? (
              <div className="mk-pgrid">{rows.map((v) => renderCard(v))}</div>
            ) : (
              <Table<ShopVariant>
                rows={rows}
                rowKey={(v) => v.code}
                dense={false}
                empty={emptyNode}
                columns={[
                  {
                    // The same photograph the grid card leads with; a blank frame, never
                    // another piece's picture, where the design has none.
                    header: t('shop.catalog.colImage'),
                    width: '64px',
                    render: (v) => <ThumbPreview url={v.imageUrls[0]} label={v.product ?? v.baseCode} />,
                  },
                  { header: t('shop.catalog.colVariant'), render: (v) => <code>{v.baseCode}</code> },
                  ...(anySku ? [{
                    header: t('shop.catalog.colYourSku'),
                    render: (v: ShopVariant) => <span className="mk-dim">{v.externalSku ?? dash}</span>,
                  }] : []),
                  {
                    header: t('shop.catalog.colProduct'),
                    render: (v) => <Link to={`/shop/catalog/${v.code}`}>{v.product ?? dash}</Link>,
                  },
                  {
                    header: t('shop.catalog.colCollection'),
                    render: (v) => <span className="mk-dim">{v.collection ?? dash}</span>,
                  },
                  {
                    /* One "Metal" column, not Purity and Colour split into two —
                       matches the reference's exact 7-column set
                       (retailer-portal.html's `tbl()` call in `drawCat()`). */
                    header: t('shop.catalog.colMetal'),
                    render: (v) => (
                      <span className="mk-dim">{[v.purity, v.metalColour].filter(Boolean).join(' ') || dash}</span>
                    ),
                  },
                  {
                    header: t('shop.catalog.colStone'),
                    render: (v) => (
                      <span className="mk-dim">{v.totalCaratWeight ? carats(v.totalCaratWeight) : dash}</span>
                    ),
                  },
                  {
                    header: t('shop.catalog.colPrice'), numeric: true,
                    render: (v) => <Money amount={v.listPrice} currency={currency} trimZeros />,
                  },
                  {
                    header: t('shop.catalog.colOrder'), numeric: true,
                    render: (v) => (
                      <AddToCart code={v.code} cartQty={cartQty.get(v.code) ?? 0} canOrder={!!canOrder} />
                    ),
                  },
                ]}
              />
            )}
          </div>
        </div>
      </Panel>

      {/* Recently viewed — its own boxed strip below the catalog, not nested
          inside it: a horizontal row of picture + name + price, scrolled by
          the track's own scrollbar or the flanking arrow buttons, with
          "Clear" wiping it (`clearLastViewed`, storage only — nothing server
          side to clear). */}
      {lastViewed.length ? (
        <Panel
          title={t('shop.catalog.lastViewedTitle')}
          actions={(
            <button
              type="button"
              className="mk-recent-clear"
              onClick={() => { clearLastViewed(); setLastViewedCodes([]); }}
            >
              {t('shop.catalog.recentClear')}
            </button>
          )}
        >
          <div className="mk-recent-row">
            <button
              type="button"
              className="mk-recent-nav"
              aria-label={t('shop.catalog.recentPrev')}
              onClick={() => scrollRecent(-1)}
            >
              ‹
            </button>
            <div className="mk-recent-track" ref={recentTrackRef}>
              {lastViewed.map((v) => renderRecentItem(v))}
            </div>
            <button
              type="button"
              className="mk-recent-nav"
              aria-label={t('shop.catalog.recentNext')}
              onClick={() => scrollRecent(1)}
            >
              ›
            </button>
          </div>
        </Panel>
      ) : null}

    
    </>
  );
}

/** R-03 — one variant. */
export function ShopVariantDetail() {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { me } = useSession();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['shop', 'variant', code],
    queryFn: () => api.get<ShopVariantDetailData>(`/shop/catalog/${code}`),
    enabled: !!code,
    // Keeps rendering the outgoing variant's full page — images, switcher,
    // buy panel, spec — while the next one loads, instead of `isLoading`
    // flipping true and blanking the whole screen to a spinner on every
    // switcher click. `pendingCode` below still marks the in-flight window
    // for the dim that says so, just without unmounting anything over it.
    placeholderData: keepPreviousData,
    // Shorter than the global 30s default (main.tsx) — see ShopCatalog above
    // for the same reasoning: this reflects admin-controlled publish/price
    // state, not the retailer's own actions.
    staleTime: 5_000,
  });
  // The switcher option the user just clicked, shown as selected immediately
  // — before the network round trip confirms it — because `target` (below)
  // is already a real, already-fetched sibling row, not a guess. Cleared by
  // the same effect that resets per-variant draft state once `data` catches
  // up to it.
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const canOrder = me?.kind === 'retailer' && me.permissions.includes('shop.order');
  const setLines = useSetCartLines();

  // Image rail selection and the buy panel's draft fields (implementation
  // spec §5) — size, remark and quantity are local until "Add to cart" is
  // pressed, never synced to the server before that. The switcher is a real
  // route change (below), so this component is never remounted between two
  // siblings — the same effect that records "last viewed" also resets this
  // draft state so it doesn't leak from one variant onto the next.
  const [activeImage, setActiveImage] = useState(0);
  /** R-03 — the full-size look. Closed on every navigation, with the rest of
   *  the per-variant draft, so it never survives onto another piece. */
  const [zoomOpen, setZoomOpen] = useState(false);
  /** The Stone dropdown selector's own draft — see `stone-selector.ts`. */
  const [stoneSel, setStoneSel] = useState<StoneSelection>({});
  /** Quantities typed in the several-at-once table, by variant code (R-04). */
  const [bulkQty, setBulkQty] = useState<Record<string, number>>({});
  /**
   * A remark per row (CR-15). Every piece in the table is a different piece, so
   * "engrave these three, leave the rest plain" has to be sayable line by line
   * — one remark for the whole table would be carried onto lines it was never
   * meant for, and Sales would have no way to tell which.
   */
  const [bulkRemark, setBulkRemark] = useState<Record<string, string>>({});
  /** CR-44 — size per row, for designs that offer one. A ring table can hold
   *  the same stone in two sizes, so this cannot be one choice for the table. */
  const [bulkSize, setBulkSize] = useState<Record<string, string>>({});
  const [bulkError, setBulkError] = useState<string | null>(null);
  /**
   * Pieces the last "Add to cart" put in the cart. The table clears on
   * success, so without this the only sign it worked was the cart badge —
   * the button just went quiet. Cleared as soon as a new quantity is typed.
   */
  const [bulkAdded, setBulkAdded] = useState<number | null>(null);
  useEffect(() => {
    if (data?.code) {
      recordLastViewed(data.code);
      setActiveImage(0);
      setPendingCode(null);
      setBulkQty({});
      setBulkRemark({});
      setBulkSize({});
      setBulkError(null);
      setBulkAdded(null);
      setZoomOpen(false);
      // Landing on a stone-bearing variant pre-fills the dropdowns with what
      // this piece actually is; landing on a plain metal one clears them —
      // there is nothing for them to describe. Only the fields this piece
      // actually carries are set — `exactOptionalPropertyTypes` refuses an
      // explicit `undefined`, and a field this piece leaves null should be
      // absent from the draft, not present-but-empty.
      const initial: StoneSelection = {};
      // One value per axis, so the table opens holding exactly the piece on
      // screen. Ticking a second clarity adds its rows beside this one —
      // which is the whole gesture: rows appear as you ask for them. Metal is
      // one purity and one colour (a single-choice select), so changing it
      // swaps the piece rather than adding one. Metal always; stone only
      // where this design has one.
      if (data.purity) initial.purity = [data.purity];
      if (data.metalColour) initial.metalColour = [data.metalColour];
      if (data.gemstone) {
        for (const { key } of STONE_FIELDS) {
          const v = data[key];
          if (v) initial[key] = [v];
        }
      }
      setStoneSel(initial);
    }
    // `data.sizeOptions` is intentionally not a dependency: it's a property of
    // the same variant `data.code` already identifies, and re-running this
    // reset whenever react-query hands back a new (but equal-content) array
    // reference would clobber the size the buyer is mid-way through picking.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.code]);

  /*
   * The metal/stone axes and the siblings-filtered-by-selection table each
   * walk `data.siblings` — up to 180 rows on the data this was measured
   * against — and none of them need to redo that walk on every keystroke in
   * a bulk-order remark box, every quantity typed, or the image rail's own
   * state changing. Only `data` itself (a new variant) or `stoneSel` (a
   * tick changing) actually invalidates them. Computed ahead of the
   * loading/error returns below (Rules of Hooks) — each falls back to
   * "nothing yet" while `data` is still undefined, which is exactly what
   * the screen shows during that render anyway.
   */
  const metalActiveFields = useMemo(
    () => activeMetalFields(data?.siblings ?? []),
    [data?.siblings],
  );
  const stoneActiveFields = useMemo(
    () => activeStoneFields(data?.stoneOptions ?? EMPTY_STONE_OPTIONS),
    [data?.stoneOptions],
  );
  const { stoneRows, stoneUnanswered } = useMemo(() => {
    if (!data) {
      return {
        stoneRows: [] as StoneSibling[],
        stoneUnanswered: [] as ReturnType<typeof unansweredFields>,
      };
    }
    const allFields = [...metalActiveFields, ...stoneActiveFields];
    const availableFor = (key: VariantField): number => {
      const metal = metalActiveFields.find((f) => f.key === key);
      if (metal) return metal.values.length;
      return stoneValuesFor(data.stoneOptions, key as StoneField, data.siblings).length;
    };
    return {
      stoneRows: matchingStoneSiblings(data.siblings, allFields, stoneSel, availableFor),
      stoneUnanswered: unansweredFields(allFields, stoneSel, availableFor),
    };
  }, [data, metalActiveFields, stoneActiveFields, stoneSel]);

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!data) return null;
  const currency = me?.kind === 'retailer' ? me.retailer.tradingCurrency : undefined;
  const attrs = Object.entries(data.attributes ?? {});
  const grams = (v: string | null) => (v ? t('shop.variant.grams', { value: trimAmount(v) }) : dash);
  const mm = (v: string) => t('shop.variant.millimetres', { value: trimAmount(v) });

  /** "Make & dimensions" (CR-48/CR-49) — product-level fields, a row only
   *  when this category actually carries that attribute (a ring has no
   *  clasp; `make.clasp` is `null` there and the row is simply absent). */
  const makeItems: Array<[string, string]> = [
    ...(data.make.setting ? [[t('shop.variant.makeSetting'), data.make.setting] as [string, string]] : []),
    ...(data.make.finish ? [[t('shop.variant.makeFinish'), data.make.finish] as [string, string]] : []),
    ...(data.make.clasp ? [[t('shop.variant.makeClasp'), data.make.clasp] as [string, string]] : []),
    ...(data.make.backing ? [[t('shop.variant.makeBacking'), data.make.backing] as [string, string]] : []),
    ...(data.make.hallmark ? [[t('shop.variant.makeHallmark'), data.make.hallmark] as [string, string]] : []),
    ...data.dimensions.map((d): [string, string] => [d.label, mm(d.millimetres)]),
  ];
  /* Rhodium plates white gold, not yellow or rose — a real, stated trade fact
   * derived from the metal colour already on the response, not a stored flag
   * (the reference does the same derivation, `retailer-portal.html:1765-1767`). */
  if (data.metalColour) {
    makeItems.push([
      t('shop.variant.rhodiumLabel'),
      data.metalColour.toLowerCase() === 'white'
        ? t('shop.variant.rhodiumPlated')
        : t('shop.variant.rhodiumNotPlated', { colour: data.metalColour.toLowerCase() }),
    ]);
  }

  /* ------------------------------------------------ the axes, CR-43/CR-46
   *
   * Every axis is a multi-select and every axis filters the same way, which is
   * how the product editor describes a variant set and now how a retailer
   * describes an order: tick two purities, two colours and two clarities and
   * the eight pieces that exist at a price become eight rows with a quantity
   * box each.
   *
   * Metal and stone differ only in where their values come from. Metal's are
   * the pieces themselves, in Admin's own sort order; stone's are what Admin
   * has offered, narrowed to what is actually priced. `metalActiveFields`,
   * `stoneActiveFields`, `stoneRows` and `stoneUnanswered` are computed
   * above, ahead of the loading/error returns — see the comment there.
   */
  const stoneNothingPicked = isStoneSelectionEmpty(stoneSel);
  /**
   * When the ticks describe exactly ONE piece, that is the piece being ordered
   * — stone is priced (CR-43), so choosing VS2 must order VS2 at the VS2 price
   * rather than the viewed piece with a note attached. Describe several and
   * this is deliberately nothing: the single Add to order cannot act on four
   * pieces, and the quantity table is the control that can.
   */
  const stoneResolved = !stoneNothingPicked && stoneRows.length === 1 ? stoneRows[0] : undefined;
  /** What the Metal & fit panel describes — the resolved piece when there is one. */
  const fitPiece = stoneResolved ?? data;
  /* Metal colour never changes price in any of the six workbooks the client
   * sent, and purity always does — so a table spanning frames carries real,
   * different numbers and the row's own price is the only one that may be
   * multiplied. That is `bulkLines` below, which reads each row's `listPrice`. */
  /* Every axis this design varies on or fixes, in display order: metal first,
   * then stone. One value -> fixed text; more -> chips. */
  const metalAll = METAL_FIELDS.map(({ key, labelKey }) => ({
    key, labelKey,
    values: metalActiveFields.find((f) => f.key === key)?.values
      ?? [...new Set(data.siblings.map((x) => x[key]).filter((v): v is string => !!v))],
  }));
  const stoneAll = stoneActiveFields.map(({ key, labelKey }) => ({
    key: key, labelKey,
    values: stoneValuesFor(data.stoneOptions, key, data.siblings),
  }));
  const allAxes = [...metalAll, ...stoneAll].filter((a) => a.values.length > 0);
  const fixedAxes = allAxes.filter((a) => a.values.length === 1)
    .map((a) => ({ ...a, value: a.values[0]! }));
  const choiceAxes = allAxes.filter((a) => a.values.length > 1);
  const currentCode = pendingCode ?? data.code;
  const goTo = (target: StoneSibling) => {
    if (target.code === data.code) return;
    setPendingCode(target.code);
    void navigate(`/shop/catalog/${target.code}`);
  };
  const prefetch = (target: StoneSibling) => {
    if (target.code === data.code) return;
    void queryClient.prefetchQuery({
      queryKey: ['shop', 'variant', target.code],
      queryFn: () => api.get<ShopVariantDetailData>(`/shop/catalog/${target.code}`),
    });
  };
  /* No pruning, and no cascade. Under multi-select, narrowing one axis because
   * another is ticked would pull values out from under a buyer mid-pass; a
   * combination with nothing behind it simply contributes no row, which the
   * table states plainly. */
  const setStoneField = (field: VariantField, value: string) => {
    setStoneSel(selectOne(
      data.siblings, [...metalActiveFields, ...stoneActiveFields], stoneSel, field, value,
    ));
    // One variant at a time (Sid, 28-Oct): a new selection is a new piece, so
    // whatever was typed against the previous one is dropped rather than kept
    // out of sight. To order two variants, add the first to the cart, then
    // choose the next.
    setBulkQty({});
    setBulkRemark({});
    setBulkSize({});
    setBulkError(null);
    setBulkAdded(null);
  };

  /**
   * The piece being ordered. Stone is a PRICED axis (CR-43), so a stone
   * selection that resolves orders THAT variant at THAT price — not the one
   * being viewed with a note attached.
   *
   * This used to send `data.code` whatever the retailer had chosen, and fold
   * the stone into a remark when nothing matched. Colette's VS2 was ordered at
   * the VVS1 price with "Requested stone: …" in the remark, and Sales saw a
   * priced line rather than a question.
   */
  /* ---------------------------------------------- several pieces at once (R-04)
   *
   * The rows are whatever the Stone multi-select above describes, within the
   * metal frame on screen. Not all 180 the product carries, and not all 20 the
   * frame carries either: a buyer ticks the two colours and two clarities they
   * want and gets four rows to put quantities against.
   */
  /* One variant at a time (Sid, 28-Oct). Every option is a single choice
   * (`selectOne`), so the selection names one piece and the table shows only
   * that piece. Rows used to be "held" when the selection moved on, so a buyer
   * working through several variants ended up with a long list under the
   * options; that is what this stops. Several variants are ordered one after
   * another: choose, add to cart, choose the next. */
  const tableRows = stoneRows;

  /* Derived from `tableRows`, not from `bulkQty` alone: only what the table is
   * SHOWING can be added, so a quantity can never be ordered invisibly. */
  const bulkLines = tableRows
    .map((r) => ({
      code: r.code, price: r.listPrice, qty: bulkQty[r.code] ?? 0,
      remark: (bulkRemark[r.code] ?? '').trim(),
      size: bulkSize[r.code] ?? data.sizeOptions[0] ?? '',
    }))
    .filter((l) => l.qty > 0);

  /**
   * One request for all of them (`PUT /shop/cart/lines/bulk`). Sent one at a
   * time this took about sixteen seconds for six rows — every cart write
   * re-reads the whole cart across the network — which is a long time to sit on
   * a screen built for picking six things.
   *
   * The API validates every code before writing any, so this is all-or-nothing:
   * either the whole table lands or the cart is untouched and the error says
   * why. There is no half-added state to explain to a buyer.
   */
  const addSeveral = () => {
    setBulkAdded(null);
    // The button stays enabled with nothing typed, so a click says what is
    // missing rather than doing nothing.
    if (bulkLines.length === 0) { setBulkError(t('shop.variant.severalEnterQty')); return; }
    setBulkError(null);
    const pieces = bulkLines.reduce((n, l) => n + l.qty, 0);
    setLines.mutate(
      {
        // `size` and `remark` are `.optional()` in the contract — omit the key
        // rather than send an empty string, which is what "not given" means to
        // Zod and what keeps a blank out of the cart line.
        lines: bulkLines.map((l) => ({
          variantCode: l.code,
          quantity: l.qty,
          ...(l.remark ? { remark: l.remark } : {}),
          ...(l.size ? { size: l.size } : {}),
        })),
      },
      {
        onSuccess: () => {
          setBulkQty({}); setBulkRemark({}); setBulkSize({});
          setBulkAdded(pieces);
        },
        onError: (e: unknown) => setBulkError(
          t('shop.variant.severalFailed', { reason: e instanceof Error ? e.message : '' }),
        ),
      },
    );
  };

  return (
    <>
      {/* The product's name is the title, its code the buy panel's own field
          (below) — not the page heading. The reference's own convention
          (retailer-portal.html's `ph(p.name, p.col+" · "+p.cat+" · made to
          order", ...)`), not the code-as-title this screen used to show. */}
      <PageHeader
        title={data.product ?? data.code}
        /* R-03 — the lowest price across the design's published variants, beside
           the name in the first screenful. The "made to order" wording this used
           to carry is stated under Production below, where it is explained. */
        aside={data.startingPrice ? (
          <span className="mk-pdp-price" title={t('shop.variant.startingPriceNote')}>
            <span className="mk-lbl">{t('shop.variant.startingFrom')}</span>{' '}
            <span className="mk-pdp-from"><IndicativePrice amount={data.startingPrice} currency={currency} trimZeros /></span>
          </span>
        ) : undefined}
        // Real back navigation, not a fixed href to a bare `/shop/catalog` —
        // that always landed on an empty search with every facet cleared,
        // whatever the catalog looked like when this piece was opened from
        // it. `navigate(-1)` returns to the exact page (and, now that the
        // catalog's own filters survive its own unmount, the exact state)
        // this was reached from.
        actions={(
          <button type="button" className="mk-btn-link" onClick={() => void navigate(-1)}>
            {t('shop.variant.back')}
          </button>
        )}
      />

      {/*
       * The reference's own two-column layout (retailer-portal.html:451-462,
       * `.pdp`/`.pdp-media`/`.pdp-stick`/`.pdp-body`) — every product page
       * shares this container, not just the configurator this build does not
       * implement (product-detail plan README). Left: a sticky media column —
       * the main image first, then a thumbnail strip BELOW it — never beside
       * it, so neither is ever sized against the other's shape, and never
       * above it, where it pushed the image down and cut off its foot. Right:
       * everything else, in one scrolling column — lede, switcher,
       * specification, the buy panel, production & ordering — the way the
       * reference stacks it, not split into side-by-side boxes.
       */}
      <Panel>
        {/* Dimmed, not hidden, while a switch is in flight — `data` here is
            still the outgoing variant's (via `placeholderData` above), so
            this is "confirming your pick," not a fresh loading state.
            Keyed on `pendingCode`, not `isFetching` — `isFetching` is also
            true for an ordinary BACKGROUND refetch of the variant already on
            screen (`staleTime` expiring, the tab regaining focus — React
            Query's default), which names nothing the reader is waiting on.
            That dimmed the whole page for no visible reason and never said
            why, and pointer-events were never disabled underneath it either,
            so it read as a stuck loading overlay sitting over an already-
            interactive page. `pendingCode` is set only for the span between
            a switcher click and the new variant landing (see the effect that
            clears it), which is the one case this dim actually explains. */}
        <div className="mk-pdp" style={{ opacity: pendingCode ? 0.6 : 1, transition: 'opacity 120ms' }}>
          <div className="mk-pdp-media">
            <div className="mk-pdp-stick">
              {/* One dedicated main-image area, always — the selected photo
                  and nothing else, and the visually prominent one, first in the
                  column so nothing above it pushes it down (the thumbnails
                  follow it). A photograph when Admin has
                  published one for this variant's metal colour (CR-43), the
                  reference's diamond placeholder when nobody has. */}
              <div className="mk-pdp-main">
                {data.imageUrls.length > 0 ? (
                  /* A button, not an image with a click handler: this opens a
                     dialog, so it has to be reachable and operable from the
                     keyboard. The magnifier is the affordance; the accessible
                     name carries the meaning. */
                  <button
                    type="button"
                    className="mk-pcard-img mk-zoomable"
                    onClick={() => setZoomOpen(true)}
                    aria-label={t('shop.variant.zoomOpen')}
                    title={t('shop.variant.zoomOpen')}
                  >
                    <img src={data.imageUrls[activeImage] ?? data.imageUrls[0]} alt="" />
                    <span className="mk-zoom-badge" aria-hidden="true">⤢</span>
                  </button>
                ) : (
                  <div className="mk-pcard-img" aria-hidden="true"><i /></div>
                )}
              </div>
              {/* The thumbnail strip, below the main image — every
                  photograph (Rose, Yellow and Silver alike; CR-43 carries
                  all of them, not just this variant's own colour), clicking
                  one only ever changes which is shown above, and
                  `aria-pressed` says which that is. Wraps rather than
                  reserving a fixed grid, and scrolls internally past a few
                  rows (`.mk-gallery-thumbs` below) so nine photographs — or
                  more — add height to a small, capped strip, never to the
                  page. */}
              {data.imageUrls.length > 1 ? (
                <div className="mk-gallery-thumbs">
                  {data.imageUrls.map((url, i) => (
                    <button
                      key={url}
                      type="button"
                      className="mk-gallery-thumb"
                      aria-pressed={i === activeImage}
                      aria-label={t('shop.variant.imageAlt', { n: i + 1, count: data.imageUrls.length })}
                      onClick={() => setActiveImage(i)}
                    >
                      <img src={url} alt="" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {zoomOpen && data.imageUrls.length > 0 ? (
            <Lightbox
              images={data.imageUrls}
              index={Math.min(activeImage, data.imageUrls.length - 1)}
              onIndex={setActiveImage}
              onClose={() => setZoomOpen(false)}
            />
          ) : null}

          <div className="mk-pdp-body">
            {/* One Options panel (R-03). An axis with a single value (Lab Grown,
                Round, D, VVS1 …) has nothing to choose, so it is plain text —
                never a control. An axis with several values (purity, metal
                colour, and any stone axis that varies) is a dropdown holding
                exactly one value; `setStoneField` brings the other axes to a
                combination that is actually made. */}
            {fixedAxes.length > 0 || choiceAxes.length > 0 ? (
              <Panel title={t('shop.variant.optionsTitle')} pad>
                {fixedAxes.length > 0 ? (
                  <Rail items={fixedAxes.map((a) => [t(a.labelKey), a.value] as [string, string])} />
                ) : null}
                {choiceAxes.length > 0 ? (
                  <div className="mk-fset">
                    {choiceAxes.map((a) => (
                      <Field label={t(a.labelKey)} key={a.key}>
                        <select
                          className="mk-fld"
                          aria-label={t(a.labelKey)}
                          value={stoneSel[a.key]?.[0] ?? ''}
                          onChange={(e) => setStoneField(a.key, e.target.value)}
                        >
                          {(stoneSel[a.key]?.[0] ?? '') === ''
                            ? <option value="" disabled>{t('shop.variant.metalChoose')}</option>
                            : null}
                          {a.values.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </Field>
                    ))}
                  </div>
                ) : null}
              </Panel>
            ) : null}

            {/*
              * R-04 in miniature, and now the ONLY way to order from this
              * screen.
              *
              * A separate "Ordering" panel used to sit above this, acting on a
              * single piece. It contradicted the table: it fell back to the
              * variant in the URL whenever the ticks described more than one
              * piece, so a buyer who had selected D/VVS1 in 10k and 14k was
              * offered H/VS2 at a different price, under a heading that gave no
              * hint the two disagreed. Two controls for one job, one of which
              * could name a piece the buyer had not chosen.
              *
              * This does everything that panel did — quantity, size (CR-44) and
              * remark (CR-15), per piece rather than one set for all of them —
              * and it does it for any number of pieces at once. A trade buyer
              * rarely wants one: they want ten of the VVS1, fifteen of the VS1
              * and five of the VS2.
              *
              * No checkboxes: a quantity above zero IS the selection. That is
              * the prototype's own model — "type a quantity beside anything you
              * want; that is the whole of it" — and one fewer state to hold.
              */}
            {canOrder && data.siblings.length > 1 ? (
              <Panel
                title={t('shop.variant.severalTitle')}
                pad
              >
                {/* What the ticks add up to, and the one thing a filter
                    cannot do on its own: load a piece. When the choices narrow
                    to a single piece, this brings its photograph, spec and
                    remark field onto the page — which is how imagery follows a
                    choice now that a choice can name more than one piece. */}
                {stoneResolved ? (
                    /* The code, price and RP are in the table below; what this adds is
                       loading the piece's own photograph and specification. */
                    stoneResolved.code === currentCode ? null : (
                      <div className="mk-switcher-grp">
                        <button
                          type="button"
                          className="mk-optbtn"
                          onMouseEnter={() => prefetch(stoneResolved)}
                          onFocus={() => prefetch(stoneResolved)}
                          onClick={() => goTo(stoneResolved)}
                        >
                          {t('shop.variant.stoneVariantView')}
                        </button>
                      </div>
                    )
                ) : !stoneNothingPicked && stoneRows.length === 0 ? (
                  // Miraki does not make this combination. Never "out of
                  // stock" — there is no stock to be out of (D4).
                  <Note>{t('shop.variant.stoneVariantNoMatch')}</Note>
                ) : !stoneNothingPicked ? (
                  <p className="mk-dim">
                    {t('shop.variant.stoneVariantMany', { count: stoneRows.length })}
                  </p>
                ) : null}
                {stoneUnanswered.length > 0 ? (
                  /* Not finished choosing. Names the axes still outstanding
                     rather than a generic note — and emphatically does not say
                     Miraki cannot make it, which is a different state and the
                     worse thing to say wrongly. */
                  <Note>
                    {t('shop.variant.severalPickFirst', {
                      fields: stoneUnanswered.map((f) => t(f.labelKey)).join(', '),
                    })}
                  </Note>
                ) : stoneRows.length === 0 ? (
                  <Note>{t('shop.variant.stoneVariantNoMatch')}</Note>
                ) : null}
                {/* Shown whenever there is a row — held rows included, so a
                    note about the current selection never hides quantities
                    already typed against another metal. */}
                {tableRows.length === 0 ? null : (
                <div className="mk-twrap">
                  <table className="mk-t mk-qty-t mk-variant-t">
                    <thead>
                      <tr>
                        <th>{t('shop.variant.colCode')}</th>
                        <th>{t('shop.variant.colCarat')}</th>
                        <th>{t('shop.variant.colWeight')}</th>
                        <th className="mk-num">{t('shop.variant.rpPrice')}</th>
                        <th className="mk-num">{t('shop.variant.price')}</th>
                        <th className="mk-num">{t('shop.variant.quantity')}</th>
                        <th className="mk-num">{t('shop.variant.lineTotal')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((r) => {
                        const q = bulkQty[r.code] ?? 0;
                        const carat = caratFromCode(r.caratCode)
                          ?? (r.totalCaratWeight ? `${trimAmount(r.totalCaratWeight)} ct` : null);
                        return (
                          <Fragment key={r.code}>
                            <tr className={q > 0 ? 'on' : undefined}>
                              <td><code>{r.code}</code></td>
                              <td>{carat ?? dash}</td>
                              <td>{r.netMetalWeightG ? `${trimAmount(r.netMetalWeightG)} g` : dash}</td>
                              {/* PR-02 — the RP is the suggested retail price, shown beside the wholesale
                                  price and never totalled (PR-03). A dash where none is set. */}
                              <td className="mk-num"><Money amount={r.rpPrice} currency={currency} /></td>
                              <td className="mk-num"><Money amount={r.listPrice} currency={currency} /></td>
                              <td className="mk-num">
                                <input
                                  className="mk-qty-in"
                                  inputMode="numeric"
                                  value={q === 0 ? '' : String(q)}
                                  placeholder="0"
                                  /* Its OWN name, not `addToCart.qtyFor`: two controls
                                     that do different things must not announce
                                     themselves identically. */
                                  aria-label={t('shop.variant.severalQtyFor', { code: r.code })}
                                  onChange={(e) => {
                                    setBulkAdded(null);
                                    setBulkError(null);
                                    setBulkQty({
                                      ...bulkQty,
                                      // A quantity is a COUNT, not money — an integer
                                      // here is correct and invariant 1 is untouched.
                                      [r.code]: Math.max(0, Math.round(Number(e.target.value.replace(/\D/g, '')) || 0)),
                                    });
                                  }}
                                />
                              </td>
                              <td className="mk-num">
                                {q > 0
                                  ? <Money amount={mulMoney(r.listPrice, q)} currency={currency} />
                                  : <span className="mk-dim">{dash}</span>}
                              </td>
                            </tr>
                            {/* Size (CR-44) and remark (CR-15) are still taken, per piece
                                — but only once a quantity is typed, on a line of their
                                own, so they stop being two permanent columns. */}
                            {q > 0 ? (
                              <tr className="on mk-variant-extra">
                                <td colSpan={7}>
                                  <div className="mk-variant-extra-in">
                                    {data.sizeOptions.length > 0 ? (
                                      <select
                                        className="mk-size-in"
                                        value={bulkSize[r.code] ?? data.sizeOptions[0] ?? ''}
                                        aria-label={t('shop.variant.severalSizeFor', { code: r.code })}
                                        onChange={(e) => setBulkSize({ ...bulkSize, [r.code]: e.target.value })}
                                      >
                                        {data.sizeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                                      </select>
                                    ) : null}
                                    <input
                                      className="mk-remark-in"
                                      value={bulkRemark[r.code] ?? ''}
                                      maxLength={1000}
                                      placeholder={t('shop.variant.remarkRowPlaceholder')}
                                      aria-label={t('shop.variant.remarkFor', { code: r.code })}
                                      onChange={(e) => setBulkRemark({ ...bulkRemark, [r.code]: e.target.value })}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}
                <div className="mk-addcart">
                  <Button
                    disabled={setLines.isPending}
                    onClick={addSeveral}
                  >
                    {/* The count is PIECES, not rows: "3" beside a GBP 41,700
                        total would read as three rings at fourteen thousand
                        each. Three rows of ten is thirty pieces, and thirty is
                        the number the buyer is committing to. */}
                    {setLines.isPending
                      ? t('shop.variant.addSeveralPending')
                      : bulkLines.length === 0
                        ? t('shop.variant.addSeveralNone')
                        : t('shop.variant.addSeveral', {
                          count: bulkLines.reduce((n, l) => n + l.qty, 0),
                        })}
                  </Button>
                  {bulkLines.length > 0 ? (
                    <span className="mk-dim">
                      <IndicativePrice amount={sumLines(bulkLines.map((l) => ({ price: l.price, qty: l.qty })))} currency={currency} trimZeros />
                    </span>
                  ) : null}
                  {bulkError ? <span className="mk-dim">{bulkError}</span> : null}
                  {bulkAdded !== null ? (
                    <span role="status">
                      {t('shop.variant.severalAdded', { count: bulkAdded })}{' '}
                      <Link to="/shop/order/review">{t('shop.variant.severalViewCart')}</Link>
                    </span>
                  ) : null}
                </div>
              </Panel>
            ) : null}

            {/* The piece the metal and stone choices point at, once they name
                exactly one — not the piece in the URL. Choosing 14k Yellow
                above while this said "10k Yellow" put two answers on one page. */}
            <Panel title={t('shop.variant.metalFitTitle')} pad>
              <Rail items={[
                /* CR-47 — three weights, because the trade asks three questions:
                   net metal prices against the gold rate, gross goes on the customs
                   declaration, total carat is every stone summed. */
                [t('shop.variant.netWeight'), grams(fitPiece.netMetalWeightG)],
                [t('shop.variant.grossWeight'), grams(fitPiece.grossWeightG)],
                [t('shop.variant.totalCarat'),
                  fitPiece.totalCaratWeight ? t('shop.variant.carats', { value: trimAmount(fitPiece.totalCaratWeight) }) : dash],
                ...attrs.map(([k, v]) => [k, String(v)] as [string, string]),
              ]} />
            </Panel>

            {/* CR-48/CR-49 — product-level, already admin-visible; never
                shown before now on the retailer side. */}
            {makeItems.length > 0 ? (
              <Panel title={t('shop.variant.makeTitle')} pad>
                <Rail items={makeItems} />
              </Panel>
            ) : null}

            {/* Factual, not commercial — made-to-order and the lead time are
                already stated elsewhere (the catalog note, OR-16/MF-02), just
                not on this screen. Unlike RRP or the disclaimer, nothing here
                is invented. */}
            <Panel title={t('shop.variant.productionTitle')} pad>
              <Rail items={[
                [t('shop.variant.madeToOrder'), t('shop.variant.madeToOrderValue')],
                [t('shop.variant.leadTime'), t('shop.variant.leadTimeValue')],
                [t('shop.variant.productionBegins'), t('shop.variant.productionBeginsValue')],
              ]} />
            </Panel>
          </div>
        </div>
      </Panel>

      {/* CR-23 — same collection first, then same category, then anything
          else, self excluded, capped at 4; already resolved server-side. */}
      {data.related.length > 0 ? (
        <div className="mk-lastviewed">
          <div className="mk-lastviewed-title">{t('shop.variant.alsoLikeTitle')}</div>
          <div className="mk-pgrid">
            {data.related.map((r) => (
              <div className="mk-pcard" key={r.code}>
                {r.imageUrls[0] ? (
                  <div className="mk-pcard-img">
                    <img src={r.imageUrls[0]} alt="" loading="lazy" />
                    <Link className="mk-pcard-hit" to={`/shop/catalog/${r.code}`} tabIndex={-1} aria-hidden="true" />
                  </div>
                ) : (
                  <div className="mk-pcard-img">
                    <i aria-hidden="true" />
                    <Link className="mk-pcard-hit" to={`/shop/catalog/${r.code}`} tabIndex={-1} aria-hidden="true" />
                  </div>
                )}
                <div className="mk-pcard-b">
                  <Link className="mk-pcard-n" to={`/shop/catalog/${r.code}`}>{r.product ?? dash}</Link>
                  <div className="mk-pcard-p"><IndicativePrice amount={r.listPrice} currency={currency} suffix trimZeros /></div>
                  <div className="mk-pcard-code"><code>{r.code}</code></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

/** R-16 — the statement. */
export function ShopStatement() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['shop', 'statement'],
    queryFn: () => api.get<Statement>('/shop/statement'),
  });
  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!data) return null;

  return (
    <>
      <PageHeader title={t('shop.statement.title')} />
      <Panel>
        <Table
          rows={data.lines}
          rowKey={(l) => `${l.date}-${l.reference}`}
          empty={<Empty title={t('shop.statement.emptyTitle')} body={data.note ?? undefined} />}
          columns={[
            { header: t('shop.statement.colDate'), render: (l) => <span className="mk-dim">{l.date.slice(0, 10)}</span> },
            { header: t('shop.statement.colType'), render: (l) => l.type.replace(/_/g, ' ').toLowerCase() },
            { header: t('shop.statement.colReference'), render: (l) => l.reference },
            { header: t('shop.statement.colDebit'), numeric: true, render: (l) => <Money amount={l.debit} /> },
            { header: t('shop.statement.colCredit'), numeric: true, render: (l) => <Money amount={l.credit} /> },
            { header: t('shop.statement.colBalance'), numeric: true, render: (l) => <Money amount={l.balance} currency={l.currency} /> },
          ]}
        />
      </Panel>
      <Note>
        <b><Money amount={data.closingBalance} /></b> — {t('shop.statement.closingNote')}
      </Note>
    </>
  );
}

/** R-18 — company profile and the terms you trade on. */
/** One postal address, a line each; a part the record does not hold is simply absent. */
function AddressBlock({ a, showDefault = false }: { a: ShopAddress; showDefault?: boolean }) {
  const { t } = useTranslation();
  /* Each part under its own name, so "US 8765" is never left to be guessed at: it is a country
     code, a postal code, a city, a phone. Only the parts the record holds are shown. */
  const rows: Array<[string, string | null]> = [
    [t('shop.account.addrContact'), a.contactName],
    [t('shop.account.addrLine1'), a.line1],
    [t('shop.account.addrLine2'), a.line2],
    [t('shop.account.addrCity'), a.city],
    [t('shop.account.addrPostal'), a.postalCode],
    [t('shop.account.addrCountry'), a.countryCode],
    [t('shop.account.addrPhone'), a.contactPhone],
  ];
  return (
    <div>
      {showDefault && a.isDefault ? <Chip tone="done">{t('shop.account.defaultAddress')}</Chip> : null}
      <Rail items={rows.filter((r): r is [string, string] => !!r[1])} />
    </div>
  );
}

export function ShopAccountScreen() {
  const { t } = useTranslation();
  const { me } = useSession();
  const { data, isLoading, error } = useQuery({
    queryKey: ['shop', 'account'],
    queryFn: () => api.get<ShopAccount>('/shop/account'),
  });
  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!data) return null;

  // D75 — the rule in force and where it came from, derived once from the one formatter.
  const dueRule = dueRuleWords(data.dueRule);
  const dueRuleFrom = dueRuleSourceWords(data.dueRule, 'retailer');

  return (
    <>
      <PageHeader
        title={data.name}
        sub={[data.city, data.countryCode].filter(Boolean).join(', ')}
        actions={<Chip tone={data.status === 'ACTIVE' ? 'done' : 'stop'}>{data.status.replace('_', ' ')}</Chip>}
      />
      {data.status === 'ON_HOLD' ? (
        <Alert tone="stop">
          <b>{t('shop.account.onHoldTitle')}</b> {t('shop.account.onHoldBody')}
        </Alert>
      ) : null}

      <div className="mk-grid2">
        <Panel title={t('shop.account.company')} pad>
          <Rail items={[
            [t('shop.account.legalName'), data.name],
            /* The label is the country pack's, so this never hardcodes "VAT ID"
               — and it is not translated here for the same reason: it is data. */
            [data.registration.label,
              data.registration.value ?? <span className="mk-dim">{t('common.notRecorded')}</span>],
            [t('shop.account.country'), data.countryCode],
            [t('shop.account.tradingCurrency'), data.tradingCurrency],
          ]} />
        </Panel>

        <Panel title={t('shop.account.terms')} pad>
          {/*
            * D75 — the payment terms line sits OUTSIDE the `data.terms` test, because the rule
            * in force does not depend on this account having a `mki_trading_terms` row: an
            * account with no row takes the portal's rule, and that is still an answer the
            * retailer is entitled to read.
            *
            * The sentence comes from `dueRuleWords()`, never built here. It read `Net {days}`
            * off `paymentTermsDays` with a 0 branch for "Prepayment", and both were wrong in the
            * same way — the account's term decided nothing, so an account on a MONTH_END portal
            * was shown "Net 30" while its invoices fell due month end plus seven. The resolved
            * rule carries its SHAPE, so a fallback reads as month end plus grace, and a net-0
            * term reads as net 0 from the one formatter rather than a second word for it here.
            */}
          <Rail items={[
            [t('shop.account.paymentTerms'), <>
              {t(dueRule.key, dueRule.values)}{' '}
              <span className="mk-dim">{t(dueRuleFrom.key)}</span>
            </>],
          ]} />
          {data.terms ? (
            <Rail items={[
              /* Advance and Incoterm are not listed (retired at v1.6). The credit limit shown below is the budget Admin
                 assigned — the one figure the account is capped by (BG-01) — not a separate stored limit. */
              [t('shop.account.creditLimit'), data.terms.creditLimit === null
                ? <span className="mk-dim">{t('shop.account.creditLimitUnset')}</span>
                : <Money amount={data.terms.creditLimit} currency={data.terms.creditLimitCurrency} plain />],
              [t('shop.account.accountManager'), data.terms.accountManager ?? dash],
            ]} />
          ) : (
            <Alert tone="us">
              <b>{t('shop.account.noTermsTitle')}</b> {t('shop.account.noTermsBody')}
            </Alert>
          )}
        </Panel>
      </div>

      {/* CR-57 / D105 — KYC gates the ORDER, not the account: filed here, decided on A-04. It sits
          directly under Company and Trading terms, the two panels it is evidence for. */}
      <KycPanel />

      {/* R-18 — addresses, account manager and team, all from the account's own
          records. Nothing is filled in where Miraki holds nothing: an empty state
          says so. */}
      <div className="mk-grid2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <Panel title={t('shop.account.registeredAddress')} pad>
          {data.registeredAddress
            ? <AddressBlock a={data.registeredAddress} />
            : <p className="mk-dim">{t('shop.account.noRegistered')}</p>}
          <AddressEdit kind="REGISTERED" address={data.registeredAddress} />
        </Panel>
        <Panel title={t('shop.account.billingAddress')} pad>
          {data.billingAddress
            ? <AddressBlock a={data.billingAddress} />
            : <p className="mk-dim">{t('shop.account.noBilling')}</p>}
          <AddressEdit kind="BILLING" address={data.billingAddress} />
        </Panel>
        <Panel title={data.deliveryAddresses.length > 1
          ? t('shop.account.deliveryAddresses') : t('shop.account.deliveryAddress')} pad>
          {data.deliveryAddresses.length === 0
            ? <><p className="mk-dim">{t('shop.account.noDelivery')}</p><AddressEdit kind="SHIPPING" address={null} /></>
            : data.deliveryAddresses.map((a, i) => (
              <div key={i} style={i > 0 ? { marginTop: 'var(--mk-space-4)' } : undefined}>
                <AddressBlock a={a} showDefault={data.deliveryAddresses.length > 1} />
                <AddressEdit kind="SHIPPING" address={a} />
              </div>
            ))}
        </Panel>
      </div>

      <Panel title={t('shop.account.managerCard')} pad>
        {data.accountManagerContact ? (
          <>
            <div><b>{data.accountManagerContact.name}</b></div>
            {data.accountManagerContact.email
              ? <div><a href={`mailto:${data.accountManagerContact.email}`}>{data.accountManagerContact.email}</a></div>
              : <p className="mk-dim">{t('shop.account.managerNoContact')}</p>}
          </>
        ) : <p className="mk-dim">{t('shop.account.managerNone')}</p>}
      </Panel>

      {me?.kind === 'retailer' && me.permissions.includes('shop.user_manage')
        ? <TeamPanel />
        : (
      <Panel title={t('shop.account.team')}>
        <Table<ShopAccount['team'][number]>
          rows={data.team}
          rowKey={(u) => `${u.name}|${u.email ?? ''}|${u.roles.join(',')}`}
          empty={<Empty title={t('shop.account.teamEmpty')} body="" />}
          columns={[
            {
              header: t('shop.account.name'),
              render: (u) => (
                <>
                  {u.name}{u.isYou ? <span className="mk-dim"> ({t('shop.account.teamYou')})</span> : null}
                </>
              ),
            },
            { header: t('shop.account.email'), render: (u) => u.email ?? dash },
            {
              header: t('shop.account.role'),
              render: (u) => (u.isPrimary ? t('shop.account.rolePrimaryShort') + (u.roles.length ? ` · ${u.roles.join(', ')}` : '')
                : u.roles.length ? u.roles.join(', ') : t('shop.account.teamNoRole')),
            },
            {
              header: '',
              render: (u) => (u.isActive ? null : <Chip tone="lapse">{t('shop.account.teamInactive')}</Chip>),
            },
          ]}
        />
      </Panel>
        )}

      <Panel title={t('shop.account.yourSignIn')} pad>
        <Rail items={[
          [t('shop.account.name'), data.you.name],
          [t('shop.account.email'), data.you.email],
          [t('shop.account.role'), data.you.isPrimary
            ? t('shop.account.rolePrimary')
            : t('shop.account.roleUser')],
        ]} />
        {me?.kind === 'retailer' && !me.termsAcceptedAt ? (
          <Note style={{ marginTop: 'var(--mk-space-4)' }}>{t('shop.account.termsNotAccepted')}</Note>
        ) : null}
      </Panel>

      {/*
        * R-18 — the browsers this user has trusted, and the means to revoke one.
        *
        * ON-06a trusts a device for 30 days and, until now, nothing could see or undo
        * that: a lost laptop stayed signed in for up to a month. `revoked_at` and
        * `revoked_by` existed on the table with nothing writing them. No permission is
        * needed — these are the signed-in user's own devices.
        */}
      <TrustedDevices />
    </>
  );
}
